

import React, { useState, useEffect, useRef } from "react";
import {
  Menu, Bell, User, ChevronDown, X, Circle, Search,
  UserCircle, HelpCircle, LogOut, KeyRound, RefreshCcw,
  AlertCircle, Info, CheckCircle, AlertTriangle,
  School,
  MoveLeft, UserPlus
} from "lucide-react";
import styles from "./header.module.css";
import { LogoutPopup } from "../logout-popup/LogoutPopup";
import { useLocation, useNavigate } from "react-router-dom";
import { useAccountSwitcher } from "../../hooks/useAccountSwitcher";
import { AddProfileModal } from "../add-profile-modal/AddProfileModal";
import { getSavedAccounts } from "../../utils/savedAccounts";
import { useUser } from "../../../app/providers/userContext";
import { useAppState } from "../../../app/providers/appStateContext";
import { USER_ROLES } from "../../constants/Constants";
import { useNotifications } from "../../../app/providers/notificationContext";
import { safeNotificationUrl } from "../../notifications/notificationData";
import { getImageFromUrl, getSchoolShortName } from "../../utils/utils";
import LinkedProfileSwitcher from "../../../features/authentication/components/LinkedProfileSwitcher";
import { showError } from "../../notifications/toast";

const TYPE_CONFIG = {
  INFO: { icon: Info, color: "#3b82f6", bg: "#eff6ff" },
  WARNING: { icon: AlertTriangle, color: "#f59e0b", bg: "#fffbeb" },
  ERROR: { icon: AlertCircle, color: "#dc2626", bg: "#fef2f2" },
  SUCCESS: { icon: CheckCircle, color: "#16a34a", bg: "#f0fdf4" },
  ANNOUNCEMENT: { icon: Info, color: "#7c3aed", bg: "#faf5ff" },
};
const getTypeConfig = (type) => TYPE_CONFIG[type] || TYPE_CONFIG.INFO;

const Header = ({ toggleSidebar }) => {
  const { reset, initializeAfterLogin } = useAppState();
  const { user, logout } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const userMenuRef = useRef(null);
  const notificationsRef = useRef(null);
  const userButtonRef = useRef(null);
  const { accounts, activeId, switching, switchTo, removeAccount } = useAccountSwitcher();
  const otherAccounts = accounts.filter((a) => a.id !== activeId);
  const isStudent = user?.roles?.[0] === USER_ROLES.STUDENT;
  const canGoBack = window.history.state?.idx > 0;
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isAddProfileOpen, setIsAddProfileOpen] = useState(false);
  const [isLinkedProfilesOpen, setIsLinkedProfilesOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { notifications, unreadCount, loading, error: notificationError, refresh: fetchNotifications,
    markRead, marking, dismiss: dismissNotification, enablePush, pushBusy, pushStatus } = useNotifications();
  const [globalSearch, setGlobalSearch] = useState('');
  const [logo, setLogo] = useState(null);
  useEffect(() => { setLogo(user?.schoolLogo ? getImageFromUrl(user.schoolLogo) : null); }, [user?.schoolLogo]);
  useEffect(() => {
    if (showNotifications) fetchNotifications();
  }, [showNotifications, fetchNotifications]);
  useEffect(() => {
    setShowNotifications(false);
    setShowUserMenu(false);
  }, [location.pathname, location.search]);
  useEffect(() => {
    if (!showUserMenu && !showNotifications) return undefined;
    const dismissOutside = event => {
      if (!userMenuRef.current?.contains(event.target)) setShowUserMenu(false);
      if (!notificationsRef.current?.contains(event.target)) setShowNotifications(false);
    };
    const dismissOnEscape = event => {
      if (event.key !== "Escape") return;
      setShowUserMenu(false);
      setShowNotifications(false);
      if (showUserMenu) userButtonRef.current?.focus();
    };
    document.addEventListener("pointerdown", dismissOutside, true);
    document.addEventListener("focusin", dismissOutside);
    document.addEventListener("keydown", dismissOnEscape);
    return () => {
      document.removeEventListener("pointerdown", dismissOutside, true);
      document.removeEventListener("focusin", dismissOutside);
      document.removeEventListener("keydown", dismissOnEscape);
    };
  }, [showUserMenu, showNotifications]);

  // ── Mark read ───────────────────────────────────────────
  const markAsRead = async (notification) => {
    if (!notification.isRead && !await markRead([notification.id])) return;
    if (notification.actionUrl) {
      navigate(safeNotificationUrl(notification.actionUrl));
      setShowNotifications(false);
    }
  };

  const markAllAsRead = async () => {
    await markRead(notifications.filter(n => !n.isRead).map(n => n.id));
  };

  // ── Helpers ─────────────────────────────────────────────
  const handleNotificationClick = () => {
    setShowNotifications(v => !v);
    setShowUserMenu(false);
  };
  const handleUserClick = () => {
    setShowUserMenu(v => !v);
    setShowNotifications(false);
  };
  const handleMenuKeyDown = (event, action) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      action();
    }
  };
  const closeAll = () => { setShowNotifications(false); setShowUserMenu(false); };

  const handleGlobalSearch = (event) => {
    event.preventDefault();
    const query = globalSearch.trim();
    if (!query) return;

    const role = user?.roles?.[0]?.toUpperCase();
    const searchPath = role === USER_ROLES.STUDENT
      ? '/student/assignment'
      : role === USER_ROLES.TEACHER
        ? '/teacher/students'
        : role === USER_ROLES.SUPER_ADMIN
          ? '/super_admin/school'
          : '/admin/student';
    navigate(`${searchPath}?search=${encodeURIComponent(query)}`);
    setGlobalSearch('');
  };

  const handleLogout = () => {
    if (activeId) removeAccount(activeId);
    logout();
    reset();
    localStorage.removeItem("user_id");
    navigate("/login", { replace: true });
    setIsPopupOpen(false);
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return "Just now";
    if (diff < 3600) return Math.floor(diff / 60) + " min ago";
    if (diff < 86400) return Math.floor(diff / 3600) + (diff >= 7200 ? " hours ago" : " hour ago");
    return Math.floor(diff / 86400) + (diff >= 172800 ? " days ago" : " day ago");
  };

  // ── Render ──────────────────────────────────────────────
  return (
    <>

      <header className={`${styles.header} sticky-top`}>
        <div className={styles.headerInner}>
          <div className={styles.headerBar}>

            {/* Left */}
            <div className={styles.headerLeft}>
              <button
                className={`${styles.menuBtn} d-lg-none`}
                onClick={toggleSidebar}
                aria-label="Toggle sidebar"
              >
                <Menu size={20} />
              </button>
              
              <button
                className={`${styles.menuBtn} d-none d-md-flex align-items-center me-4`}
                onClick={() => {
                  if (canGoBack) {
                    navigate(-1);
                  } else {
                    window.location.reload();
                  }
                }}
                aria-label={canGoBack ? "Go back" : "Refresh page"}
                title={canGoBack ? "Go back" : "Refresh page"}
              >
                
                <MoveLeft size={20} />
              </button>       <div className={styles.logoContainer}>
                <div className={styles.logo}>
                  {logo ? (
                    <img
                      src={logo}
                      alt="School Logo"
                      className={styles.logoImage}
                      onError={() => setLogo(null)}
                    />
                  ) : user?.schoolName ? (
                    <div className={styles.logoText}>
                      {getSchoolShortName(user.schoolName)}
                    </div>
                  ) : (
                    <School size={22} />
                  )}
                </div>

                <div className={styles.titleContainer}>
                  <h1 className={styles.headerTitle}>
                    {user?.schoolName || "School Management"}
                  </h1>
                </div>
              </div>
            </div>

            <form className={`${styles.globalSearch} d-none d-md-flex`} onSubmit={handleGlobalSearch} role="search">
              <Search size={16} aria-hidden="true" />
              <input
                value={globalSearch}
                onChange={(event) => setGlobalSearch(event.target.value)}
                placeholder="Search..."
                aria-label="Search the application"
              />
              <kbd>Enter</kbd>
            </form>

            {/* Right */}
            <div className={styles.headerRight}>
              {isStudent && <button type="button" className={`attention-ring ${styles.profileSwitchBtn}`} onClick={() => setIsLinkedProfilesOpen(true)} aria-label={`Switch student profile, current profile ${user?.name || user?.username || "Student"}`}>
                <UserCircle size={18} aria-hidden="true" />
                <span>Switch child</span>
              </button>}

              {/* Bell */}
              <div ref={notificationsRef} className={styles.notification}>
                <button
                  onClick={handleNotificationClick}
                  aria-label="Notifications"
                  className={unreadCount > 0 ? "attention-ring" : undefined}
                  style={{ position: "relative" }}
                >
                  <Bell size={20} />
                  {unreadCount > 0 && (
                    <span style={{
                      position: "absolute", top: 2, right: 2,
                      background: "#ef4444", color: "#fff",
                      borderRadius: "50%", fontSize: "0.6rem",
                      minWidth: 16, height: 16,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontWeight: 700, lineHeight: 1, padding: "0 3px",
                    }}>
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </button>

                {showNotifications && (
                  <div className={styles.notificationDropdown}>
                    {/* Header */}
                    <div style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      padding: "12px 16px", borderBottom: "1px solid #e2e8f0",
                      background: "#f8fafc", borderRadius: "14px 14px 0 0",
                      position: "sticky", top: 0, zIndex: 1,
                    }}>
                      <span style={{ fontWeight: 700, fontSize: "0.9rem", color: "#1e293b" }}>
                        Notifications
                        {unreadCount > 0 && (
                          <span style={{
                            marginLeft: 8, background: "#ef4444", color: "#fff",
                            borderRadius: 99, fontSize: "0.65rem", padding: "1px 6px", fontWeight: 700,
                          }}>{unreadCount}</span>
                        )}
                      </span>
                      {notifications.some(n => !n.isRead) && (
                        <button
                          onClick={markAllAsRead}
                          disabled={marking}
                          style={{ background: "none", border: "none", color: "#3b82f6", fontSize: "0.78rem", cursor: "pointer", fontWeight: 600, padding: 0 }}
                        >
                          Mark shown as read
                        </button>
                      )}
                    </div>

                    {/* Body */}
                    {loading ? (
                      <div style={{ textAlign: "center", padding: "24px", color: "#94a3b8", fontSize: "0.85rem" }}>
                        Loading...
                      </div>
                    ) : null}
                    {notificationError && (
                      <div style={{ textAlign: "center", padding: "24px 16px", color: "#b91c1c", fontSize: "0.85rem" }} role="status">
                        <div>{notificationError}</div>
                        <button
                          type="button"
                          onClick={fetchNotifications}
                          style={{ marginTop: 8, border: 0, background: "none", color: "#2563eb", fontWeight: 600, cursor: "pointer" }}
                        >
                          Try again
                        </button>
                      </div>
                    )}
                    {!loading && notifications.length === 0 ? (
                      <div style={{ textAlign: "center", padding: "32px 16px", color: "#94a3b8" }}>
                        <div style={{ fontSize: 28, marginBottom: 8 }}>📭</div>
                        <div style={{ fontSize: "0.85rem" }}>No notifications</div>
                      </div>
                    ) : !loading && (
                      notifications.map((n) => {
                        const { icon: Icon, color, bg } = getTypeConfig(n.type);
                        return (
                          <div
                            key={n.id}
                            onClick={() => markAsRead(n)}
                            onKeyDown={(event) => handleMenuKeyDown(event, () => markAsRead(n))}
                            role="button"
                            tabIndex={0}
                            style={{
                              display: "flex", gap: 10, padding: "10px 14px",
                              borderBottom: "1px solid #f1f5f9",
                              background: n.isRead ? "#fff" : "#f0f7ff",
                              borderLeft: `3px solid ${color}`,
                              cursor: "pointer", transition: "background 0.15s",
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = n.isRead ? "#f8fafc" : "#e0f0ff"}
                            onMouseLeave={e => e.currentTarget.style.background = n.isRead ? "#fff" : "#f0f7ff"}
                          >
                            <div style={{
                              width: 30, height: 30, borderRadius: "50%",
                              background: bg, display: "flex", alignItems: "center",
                              justifyContent: "center", flexShrink: 0, marginTop: 2,
                            }}>
                              <Icon size={14} color={color} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 6 }}>
                                <div style={{
                                  fontSize: "0.82rem", fontWeight: n.isRead ? 400 : 600,
                                  color: "#1e293b", overflow: "hidden",
                                  display: "-webkit-box", WebkitLineClamp: 1,
                                  WebkitBoxOrient: "vertical",
                                }}>
                                  {!n.isRead && <Circle size={6} fill="#3b82f6" color="#3b82f6" style={{ marginRight: 4, verticalAlign: "middle" }} />}
                                  {n.title}
                                </div>
                                <button
                                  type="button"
                                  onClick={e => { e.stopPropagation(); dismissNotification(n.id); }}
                                  aria-label={`Dismiss notification: ${n.title}`}
                                  style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer", padding: 0, flexShrink: 0, lineHeight: 1 }}
                                >
                                  <X size={12} />
                                </button>
                              </div>
                              <div style={{ fontSize: "0.77rem", color: "#64748b", marginTop: 2, lineHeight: 1.4 }}>
                                {n.message}
                              </div>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
                                <span style={{ fontSize: "0.72rem", color: "#94a3b8" }}>{formatTime(n.createdAt)}</span>
                                {n.actionRequired && (
                                  <span style={{ fontSize: "0.68rem", background: bg, color, borderRadius: 99, padding: "1px 7px", fontWeight: 700 }}>
                                    Action Required
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}

                    {/* Footer */}
                    <div style={{
                      textAlign: "center", padding: "10px",
                      borderTop: "1px solid #e2e8f0",
                      position: "sticky", bottom: 0,
                      background: "#fff", borderRadius: "0 0 14px 14px",
                    }}>
                      <button
                        type="button"
                        onClick={fetchNotifications}
                        style={{
                          background: "none", border: "none", color: "#3b82f6",
                          fontSize: "0.82rem", fontWeight: 600, cursor: "pointer",
                        }}
                      >
                        Refresh latest 20 notifications
                      </button>
                      <button type="button" onClick={enablePush} disabled={pushBusy}
                        style={{ display: "block", minHeight: 44, margin: "4px auto", border: 0, background: "none", color: "#2563eb" }}>
                        {pushBusy ? "Enabling..." : "Enable browser notifications"}
                      </button>
                      {pushStatus && <p role="status" style={{ fontSize: "0.8rem", margin: 4 }}>{pushStatus}</p>}
                    </div>
                  </div>
                )}
              </div>

              {/* User pill */}
              <div ref={userMenuRef} className={styles.user}>
                <button type="button" ref={userButtonRef} className={styles.userTrigger}
                  onClick={handleUserClick} aria-label="Account menu"
                  aria-expanded={showUserMenu} aria-controls="header-account-menu">
                  <User size={16} />
                  <span className="d-none d-sm-inline" style={{ fontSize: "0.85rem" }}>{user?.name}</span>
                  <ChevronDown size={14} style={{ opacity: 0.6 }} />
                </button>

                {showUserMenu && (
                  <div id="header-account-menu" role="region" aria-label="Account menu" className={styles.userDropdown}>
                    {/* User info header */}
                    <div className={styles.userDropdownHeader}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{
                          width: 36, height: 36, borderRadius: "50%",
                          background: "linear-gradient(135deg,#3b82f6,#1d4ed8)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          flexShrink: 0,
                        }}>
                          <User size={18} color="#fff" />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div className={styles.userDropdownName}>
                            {user?.name || user?.username || "User"}
                          </div>
                          <div className={styles.userDropdownEmail}>
                            {user?.email || ""}
                          </div>
                        </div>
                      </div>
                    </div>

                    {(isStudent || otherAccounts.length > 0) && (
                      <div style={{ padding: "6px 0", borderBottom: "1px solid #e2e8f0" }}>
                        <div style={{ padding: "4px 16px", fontSize: 11, fontWeight: 600, color: "#64748b", textTransform: "uppercase" }}>
                          Switch profile
                        </div>
                        {otherAccounts.map((account) => (
                          <button
                            key={account.id}
                            className="dropdown-item d-flex align-items-center gap-2 py-2 px-3"
                            style={{ minHeight: 44 }}
                            disabled={switching}
                            onClick={() => { switchTo(account).catch(err => showError(err.message || "Could not switch profiles.")); closeAll(); }}
                          >
                            <UserCircle size={18} color="#3b82f6" />
                            <span style={{ minWidth: 0, textAlign: "left" }}>
                              <span style={{ display: "block", fontSize: 14 }}>{account.userData?.name || account.userData?.username}</span>
                              <span style={{ display: "block", fontSize: 11, color: "#64748b" }}>{account.userData?.schoolName}</span>
                            </span>
                          </button>
                        ))}
                        {isStudent && (
                          <button type="button"
                            className="dropdown-item d-flex align-items-center gap-2 py-2 px-3"
                            style={{ minHeight: 44 }}
                            onClick={() => { setIsLinkedProfilesOpen(true); closeAll(); }}>
                            <UserCircle size={18} color="#64748b" /> Students linked to my mobile
                          </button>
                        )}
                        {isStudent && (
                          <button
                            className="dropdown-item d-flex align-items-center gap-2 py-2 px-3"
                            style={{ minHeight: 44 }}
                            onClick={() => { setIsAddProfileOpen(true); closeAll(); }}
                          >
                            <UserPlus size={16} color="#64748b" /> Add another child
                          </button>
                        )}
                      </div>
                    )}

                    {/* Menu items */}
                    <div style={{ padding: "6px 0" }}>
                      {user?.roles && (user.roles[0] === USER_ROLES.STUDENT || user.roles[0] === USER_ROLES.TEACHER) && (
                        <button className="dropdown-item d-flex align-items-center gap-2 py-2 px-3"
                          onClick={() => { navigate(user.roles[0] === USER_ROLES.STUDENT ? "/student/profile" : "/teacher/profile"); closeAll(); }}>
                          <UserCircle size={15} color="#64748b" /> My Profile
                        </button>
                      )}
                      <button className="dropdown-item d-flex align-items-center gap-2 py-2 px-3"
                        onClick={() => { navigate("/support"); closeAll(); }}>
                        <HelpCircle size={15} color="#64748b" /> Help & Support
                      </button>
                      <button className="dropdown-item d-flex align-items-center gap-2 py-2 px-3"
                        onClick={() => { reset(); initializeAfterLogin(); closeAll(); }}>
                        <RefreshCcw size={15} color="#64748b" /> Refresh Data
                      </button>
                      <button className="dropdown-item d-flex align-items-center gap-2 py-2 px-3"
                        onClick={() => { navigate("/setting/change-password"); closeAll(); }}>
                        <KeyRound size={15} color="#64748b" /> Change Password
                      </button>
                      <div style={{ height: 1, background: "#e2e8f0", margin: "4px 0" }} />
                      <button
                        className="dropdown-item d-flex align-items-center gap-2 py-2 px-3"
                        style={{ color: "#ef4444" }}
                        onClick={() => { setIsPopupOpen(true); closeAll(); }}
                      >
                        <LogOut size={15} color="#ef4444" /> Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <AddProfileModal
        isOpen={isAddProfileOpen}
        onClose={() => setIsAddProfileOpen(false)}
        onAdded={(id) => {
          const added = getSavedAccounts().find((a) => a.id === id);
          if (added) switchTo(added).catch(err => showError(err.message || "Could not switch profiles."));
        }}
      />
      {isLinkedProfilesOpen && <LinkedProfileSwitcher onClose={() => setIsLinkedProfilesOpen(false)} />}

      <LogoutPopup
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        onConfirm={handleLogout}
        userInfo={user}
      />
    </>
  );
};

export default Header;